// File: src/Partials/Universal/Dashboard/Index.jsx
import React, { useEffect, useState, useMemo } from "react";
import Chart from "react-apexcharts";

import { GetDashboardData } from "adminApi/DashboardApi/Dashboard";
import BookingTable from "./Components/BookingTable";
import {
  Users,
  CalendarCheck,
  DollarSign,
  TrendingUp,
  ArrowUpRight,
  Hotel
} from "lucide-react";

// ─────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────
const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US").format(Number(value) || 0);

// ─────────────────────────────────────────────────────
// Dashboard Component
// ─────────────────────────────────────────────────────
const Index = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ cards: null, chart: null });

  // ── Fetch Dashboard Data ──────────────────────────
  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await GetDashboardData(controller.signal);
        if (result?.success) {
          setData(result.data);
        }
      } catch (error) {
        console.error("Dashboard Fetch Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, []);

  // ── Card Config ───────────────────────────────────
  const cardData = useMemo(() => {
    const cards = data.cards || {
      todayBooking: 0,
      todayRevenue: 0,
      totalCustomer: 0,
      totalRevenue: 0
    };

    return [
      {
        title: "Today's Bookings",
        value: formatNumber(cards.todayBooking),
        trend: "Real-time",
        icon: <CalendarCheck size={24} className="text-indigo" />,
        color: "indigo",
        bg: "rgba(99, 102, 241, 0.15)",
        borderColor: "#6366f1"
      },
      {
        title: "Today's Revenue",
        value: formatCurrency(cards.todayRevenue),
        trend: "Today",
        icon: <DollarSign size={24} className="text-emerald" />,
        color: "emerald",
        bg: "rgba(16, 185, 129, 0.15)",
        borderColor: "#10b981"
      },
      {
        title: "Total Customer",
        value: formatNumber(cards.totalCustomer),
        trend: "Active",
        icon: <Users size={24} className="text-amber" />,
        color: "amber",
        bg: "rgba(245, 158, 11, 0.15)",
        borderColor: "#f59e0b"
      },
      {
        title: "Total Revenue",
        value: formatCurrency(cards.totalRevenue),
        trend: "Life-time",
        icon: <TrendingUp size={24} className="text-rose" />,
        color: "rose",
        bg: "rgba(244, 63, 94, 0.15)",
        borderColor: "#f43f5e"
      }
    ];
  }, [data.cards]);

  // ── Chart Config ──────────────────────────────────
  const chartOptions = useMemo(() => {
    const labels = data.chart?.labels || [];

    return {
      chart: {
        height: 320,
        type: 'line',
        toolbar: { show: false },
        fontFamily: "'Inter', sans-serif",
        animations: { enabled: false }, // Static as per user earlier request
        zoom: { enabled: false }
      },
      colors: ["#6366f1", "#10b981"],
      stroke: {
        width: [0, 2],
        curve: 'smooth'
      },
      plotOptions: {
        bar: {
          columnWidth: '40%',
          borderRadius: 4
        }
      },
      fill: {
        type: ['solid', 'gradient'],
        gradient: {
          shade: 'light',
          type: "vertical",
          shadeIntensity: 0.5,
          opacityFrom: 0.4,
          opacityTo: 0,
          stops: [0, 90, 100]
        }
      },
      labels: labels,
      xaxis: {
        type: 'category',
        labels: { style: { colors: '#64748b', fontSize: '12px' } }
      },
      yaxis: [
        {
          title: { text: 'Bookings', style: { color: '#6366f1', fontWeight: 600 } },
          labels: { style: { colors: '#64748b' }, formatter: (val) => val.toFixed(0) }
        },
        {
          opposite: true,
          title: { text: 'Revenue', style: { color: '#10b981', fontWeight: 600 } },
          labels: { style: { colors: '#64748b' }, formatter: (val) => formatCurrency(val) }
        }
      ],
      tooltip: {
        shared: true,
        intersect: false,
        theme: 'light',
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right'
      },
      grid: {
        borderColor: "#e2e8f0",
        strokeDashArray: 4,
      }
    };
  }, [data.chart]);

  const chartSeries = useMemo(() => {
    const chartData = data.chart || { bookings: [], revenue: [] };
    return [
      { name: "Bookings Volume", type: "column", data: chartData.bookings },
      { name: "Daily Revenue ($)", type: "area", data: chartData.revenue },
    ];
  }, [data.chart]);

  // ── Loading Skeleton ──────────────────────────────
  if (loading) {
    return (
      <div className="px-xl-5 px-lg-4 px-3 py-4 page-body">
        <div className="row g-3 mb-4">
          {[1, 2, 3, 4].map((i) => (
            <div className="col-md-6 col-lg-3" key={i}>
              <div className="card border-0 shadow-sm" style={{ borderRadius: "16px" }}>
                <div className="card-body p-4 placeholder-glow">
                  <div className="placeholder col-4 mb-3" style={{ height: "40px", borderRadius: "12px" }}></div>
                  <div className="placeholder col-8 mb-2" style={{ height: "24px" }}></div>
                  <div className="placeholder col-6" style={{ height: "16px" }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="card border-0 shadow-sm" style={{ borderRadius: "16px" }}>
          <div className="card-body p-5 d-flex align-items-center justify-content-center" style={{ height: 400 }}>
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Render ───────────────────────────────────
  return (
    <div className="px-xl-5 px-lg-4 px-3 py-4 page-body bg-light-subtle">
      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">Performance Dashboard</h4>
          <p className="text-muted small mb-0">Live operational data from the hotel database.</p>
        </div>
      </div>

      {/* ── Cards ── */}
      <div className="row g-3 mb-4">
        {cardData.map((data, index) => (
          <div className="col-md-6 col-lg-3" key={index}>
            <div className="card border-0 shadow-sm h-100 overflow-hidden" style={{ borderRadius: "16px" }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center"
                    style={{ width: "48px", height: "48px", backgroundColor: data.bg }}
                  >
                    {data.icon}
                  </div>
                  <div className={`badge bg-${data.color}-subtle text-${data.color} border-0 rounded-pill px-2 py-1 small d-flex align-items-center gap-1`}>
                    <ArrowUpRight size={12} />
                    {data.trend}
                  </div>
                </div>
                <h6 className="text-muted fw-medium mb-1 small uppercase tracking-wider">{data.title}</h6>
                <div className="d-flex align-items-baseline gap-2">
                  <h3 className="fw-bold mb-0">{data.value}</h3>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3">
        {/* ── Chart Hub ── */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "16px" }}>
            <div className="card-header bg-transparent border-0 p-4 pb-0">
              <h6 className="card-title fw-bold mb-1">Bookings & Revenue Trend</h6>
              <p className="text-muted small mb-0">Daily business cycle for the last 12 days</p>
            </div>
            <div className="card-body p-4">
              <Chart
                options={chartOptions}
                series={chartSeries}
                height={320}
                type="line"
              />
            </div>
          </div>
        </div>

        {/* ── Sidebar Stats ── */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "16px" }}>
            <div className="card-header bg-transparent border-0 p-4 pb-0">
              <h6 className="card-title fw-bold mb-1">Inventory Status</h6>
              <p className="text-muted small mb-0">Current room availability</p>
            </div>
            <div className="card-body p-4">
              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 transition-hover">
                  <div className="d-flex align-items-center gap-3">
                    <div className="bg-primary-subtle p-2 rounded-3 text-primary"><Hotel size={20} /></div>
                    <div>
                      <p className="mb-0 small fw-medium">Total Units</p>
                      <h6 className="mb-0 fw-bold">{data.inventory?.total || 0} Rooms</h6>
                    </div>
                  </div>
                </div>

                <div className="row g-2 mb-2">
                  <div className="col-4">
                    <div className="p-2 border rounded-3 text-center bg-success-subtle border-success-subtle">
                      <p className="small text-success fw-bold mb-0 uppercase-xs">Available</p>
                      <h5 className="mb-0 fw-bold text-success">{data.inventory?.breakdown?.find(r => r.room_status === 1)?.count || 0}</h5>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-2 border rounded-3 text-center bg-danger-subtle border-danger-subtle">
                      <p className="small text-danger fw-bold mb-0 uppercase-xs">Occupied</p>
                      <h5 className="mb-0 fw-bold text-danger">{data.inventory?.breakdown?.find(r => r.room_status === 2)?.count || 0}</h5>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="p-2 border rounded-3 text-center bg-warning-subtle border-warning-subtle">
                      <p className="small text-warning fw-bold mb-0 uppercase-xs">Maintenance</p>
                      <h5 className="mb-0 fw-bold text-warning">{data.inventory?.breakdown?.find(r => r.room_status === 3)?.count || 0}</h5>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-primary-subtle rounded-3 border border-primary-subtle">
                  <div className="d-flex justify-content-between mb-2">
                    <p className="small text-primary fw-bold mb-0">Occupancy Rate</p>
                    <p className="small text-primary fw-bold mb-0">
                      {data.inventory?.total > 0
                        ? Math.round(((data.inventory?.breakdown?.find(r => r.room_status === 2)?.count || 0) / data.inventory.total) * 100)
                        : 0}%
                    </p>
                  </div>
                  <div className="progress overflow-visible" style={{ height: "8px", backgroundColor: "rgba(var(--primary-rgb), 0.2)" }}>
                    <div
                      className="progress-bar bg-primary rounded-pill shadow-sm"
                      style={{
                        width: `${data.inventory?.total > 0 ? Math.round(((data.inventory?.breakdown?.find(r => r.room_status === 2)?.count || 0) / data.inventory.total) * 100) : 0}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Arrival List Table ── */}
        <div className="col-12 mt-3">
          <div className="card border-0 shadow-sm" style={{ borderRadius: "16px" }}>
            <div className="card-header bg-transparent border-0 p-4 pb-0">
              <h6 className="card-title fw-bold mb-1">Today's Arrivals</h6>
              <p className="text-muted small mb-0">Managing guest stays and bookings</p>
            </div>
            <div className="card-body p-4">
              <BookingTable />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
